import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "columns", "data", "onRequestSort", "rowHeader", "sort", "emptyContent", "headerRenderer", "a11yLabels", "mobileViewBreakpoint", "stickyHeaderOffset", "rowMinHeight", "getItemKey", "pl", "pr", "align"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.sort.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React from 'react';
import { useTranslation } from 'react-i18next';
import CenterContent from 'components/CenterContent';
import Flex from 'components/Flex';
import Text from 'components/Text';
import { getColumnName, getCellContent, getColumnConfigFromChildren, getDefaultItemKey } from './utils';
import { TableContextProvider, TableWrapper, TableColumnHeader, TableHead, TableBody, TableRow, TableBodyCell } from './shared';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export var defaultHeaderRenderer = function defaultHeaderRenderer(column) {
  return /*#__PURE__*/_jsx(Text, {
    bold: true,
    children: column.title
  });
};
export var DefaultEmptyContent = function DefaultEmptyContent() {
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  return /*#__PURE__*/_jsx(CenterContent, {
    height: 200,
    children: t('no_data_found')
  });
};
export function useDefaultA11yLabels() {
  var _useTranslation2 = useTranslation(),
    t = _useTranslation2.t;
  var sortByColumn = function sortByColumn(columnTitle, order) {
    var sortByColumnAsc = t('table_sort_by_column_ascending_order', {
      columnTitle: columnTitle
    });
    var sortByColumnDesc = t('table_sort_by_column_descending_order', {
      columnTitle: columnTitle
    });
    return order === 'asc' ? sortByColumnAsc : sortByColumnDesc;
  };
  return {
    sortedAsc: t('table_ascending'),
    sortedDesc: t('table_descending'),
    sortByColumn: sortByColumn,
    selectSingleRow: function selectSingleRow(itemLabel) {
      return t('actions_select_only', {
        item: itemLabel
      });
    },
    selectAllRows: t('actions_select_all'),
    navigationHint: t('hint_select_table_navigation')
  };
}
function Table(_ref) {
  var children = _ref.children,
    columnsProp = _ref.columns,
    data = _ref.data,
    onRequestSort = _ref.onRequestSort,
    rowHeaderProp = _ref.rowHeader,
    sort = _ref.sort,
    _ref$emptyContent = _ref.emptyContent,
    emptyContent = _ref$emptyContent === void 0 ? /*#__PURE__*/_jsx(DefaultEmptyContent, {}) : _ref$emptyContent,
    _ref$headerRenderer = _ref.headerRenderer,
    headerRenderer = _ref$headerRenderer === void 0 ? defaultHeaderRenderer : _ref$headerRenderer,
    a11yLabels = _ref.a11yLabels,
    _ref$mobileViewBreakp = _ref.mobileViewBreakpoint,
    mobileViewBreakpoint = _ref$mobileViewBreakp === void 0 ? 'xs' : _ref$mobileViewBreakp,
    _ref$stickyHeaderOffs = _ref.stickyHeaderOffset,
    stickyHeaderOffset = _ref$stickyHeaderOffs === void 0 ? 0 : _ref$stickyHeaderOffs,
    _ref$rowMinHeight = _ref.rowMinHeight,
    rowMinHeight = _ref$rowMinHeight === void 0 ? 45 : _ref$rowMinHeight,
    _ref$getItemKey = _ref.getItemKey,
    getItemKey = _ref$getItemKey === void 0 ? getDefaultItemKey : _ref$getItemKey,
    pl = _ref.pl,
    pr = _ref.pr,
    align = _ref.align,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var columns = children ? getColumnConfigFromChildren(children) : columnsProp;
  if (!columns || columns.length === 0) {
    throw new Error('Table must have either children or columns prop');
  }
  var hasData = data && data.length > 0;
  var rowHeader = rowHeaderProp || getColumnName(columns[0]);
  var defaultA11yLabels = useDefaultA11yLabels();
  var resolvedA11yLabels = _objectSpread(_objectSpread({}, defaultA11yLabels), a11yLabels);
  return /*#__PURE__*/_jsxs(TableContextProvider, _objectSpread(_objectSpread({
    mobileViewBreakpoint: mobileViewBreakpoint,
    stickyHeaderOffset: stickyHeaderOffset,
    rowMinHeight: rowMinHeight,
    pl: pl,
    pr: pr
  }, otherProps), {}, {
    children: [/*#__PURE__*/_jsxs(TableWrapper, {
      "aria-label": resolvedA11yLabels.label,
      children: [/*#__PURE__*/_jsx(TableHead, {
        children: /*#__PURE__*/_jsx(TableRow, {
          children: columns.map(function (column) {
            return /*#__PURE__*/_jsx(TableColumnHeader, {
              column: column,
              sort: sort,
              a11yLabels: resolvedA11yLabels,
              onRequestSort: onRequestSort,
              children: headerRenderer(column)
            }, getColumnName(column));
          })
        })
      }), /*#__PURE__*/_jsx(TableBody, {
        children: hasData && getItemKey && data.map(function (item) {
          return /*#__PURE__*/_jsx(TableRow, {
            align: align,
            children: columns.map(function (column) {
              var cellRenderer = column.cellRenderer,
                hideBelowBreakpoint = column.hideBelowBreakpoint,
                name = column.name,
                title = column.title;
              var columnName = getColumnName(column);
              var isHeader = [name, title].includes(rowHeader);
              return /*#__PURE__*/_jsx(TableBodyCell, {
                title: title,
                isHeader: isHeader,
                hideBelowBreakpoint: hideBelowBreakpoint,
                children: getCellContent(item, cellRenderer || columnName)
              }, columnName);
            })
          }, getItemKey(item));
        })
      })]
    }), !hasData && /*#__PURE__*/_jsx(Flex, {
      pl: pl,
      pr: pr,
      minHeight: rowMinHeight,
      align: "center",
      children: emptyContent
    })]
  }));
}
export default Table;